import React from 'react';
import clsx from 'clsx';

/**
 * LoadingSpinner - pure functional loading indicator
 * Supports both CSS animation and SVG approaches
 */
export function LoadingSpinner({ 
  size = 'medium', 
  fullPage = false,
  message = 'Loading...',
  variant = 'svg', // 'svg' or 'css'
  className = ''
}) {
  const sizeClasses = {
    tiny: 'h-2 w-2',
    small: 'h-4 w-4',
    medium: 'h-8 w-8',
    large: 'h-12 w-12'
  };

  // CSS-based spinner (migrated from app.css)
  const cssSpinner = (
    <div className="flex flex-col items-center">
      <div className={clsx(
        'animate-spin rounded-full',
        size === 'tiny' && 'w-3 h-3 border-1',
        size === 'small' && 'w-5 h-5 border-2',
        size === 'medium' && 'w-10 h-10 border-[3px]',
        size === 'large' && 'w-[60px] h-[60px] border-4',
        'border-base-300 border-t-primary'
      )} />
      {message && (
        <p className={clsx(
          'mt-4 text-base-content/70',
          size === 'small' ? 'text-xs' : size === 'large' ? 'text-lg' : 'text-sm'
        )}>
          {message}
        </p>
      )}
    </div>
  );

  // SVG-based spinner (existing implementation)
  const svgSpinner = (
    <div className={clsx("flex flex-col items-center", className)}>
      <div className={`${sizeClasses[size]} animate-spin`}>
        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
      {message && (
        <p className={`mt-3 text-base-content/70 ${
          size === 'small' ? 'text-xs' : size === 'large' ? 'text-lg' : 'text-sm'
        }`}>
          {message}
        </p>
      )}
    </div>
  );

  const spinner = variant === 'css' ? cssSpinner : svgSpinner;

  if (fullPage) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-base-100 bg-opacity-90 z-50">
        {spinner}
      </div>
    );
  }

  return spinner;
}
